import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Boxes, FolderKanban, Search } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAsync } from '@/lib/useAsync';
import { api } from '@/lib/api';

interface JumpItem {
  key: string;
  label: string;
  context: string | null;
  to: string;
  kind: 'company' | 'workspace' | 'project';
}

const ICON = { company: Building2, workspace: Boxes, project: FolderKanban } as const;

const OPEN_EVENT = 'quickjump:open';

/** Open the palette from anywhere (e.g. the top-bar search button). */
export function openQuickJump() {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

/**
 * ⌘K / Ctrl-K quick jump — type, arrow, enter. The IA scales because nothing
 * is ever more than a few keystrokes away, regardless of how many companies
 * exist. Index comes from /overview (already scoped server-side).
 */
export function QuickJump() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const overview = useAsync(() => api.getOverview(), [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
        setQuery('');
        setActive(0);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    const onOpen = () => {
      setOpen(true);
      setQuery('');
      setActive(0);
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const items = useMemo<JumpItem[]>(() => {
    if (overview.status !== 'success') return [];
    const out: JumpItem[] = [];
    for (const c of overview.data.companies) {
      out.push({ key: c.id, label: c.name, context: null, to: `/c/${c.slug}`, kind: 'company' });
      for (const w of c.workspaces) {
        out.push({
          key: w.id,
          label: w.name,
          context: c.name,
          to: `/c/${c.slug}/w/${w.slug}`,
          kind: 'workspace',
        });
        for (const p of w.projects) {
          out.push({
            key: p.id,
            label: p.name,
            context: `${c.name} · ${w.name}`,
            to: `/c/${c.slug}/w/${w.slug}/p/${p.slug}`,
            kind: 'project',
          });
        }
      }
    }
    return out;
  }, [overview.status, overview.status === 'success' ? overview.data : null]);

  const q = query.trim().toLowerCase();
  const results = (q ? items.filter((i) => (i.label + ' ' + (i.context ?? '')).toLowerCase().includes(q)) : items).slice(0, 12);

  function go(item: JumpItem) {
    setOpen(false);
    navigate(item.to);
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[14vh]" onClick={() => setOpen(false)}>
      <div
        className="w-full max-w-lg overflow-hidden rounded-lg border border-border bg-surface shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <Search className="h-4 w-4 text-fg-subtle" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
              if (e.key === 'Enter' && results[active]) go(results[active]);
            }}
            placeholder="Jump to a company, engagement, or project…"
            className="h-12 flex-1 bg-transparent text-[15px] focus:outline-none"
          />
          <kbd className="rounded-sm border border-border px-1.5 py-0.5 text-2xs text-fg-subtle">esc</kbd>
        </div>
        <ul className="max-h-[46vh] overflow-y-auto py-1.5">
          {results.map((item, i) => {
            const Icon = ICON[item.kind];
            return (
              <li key={`${item.kind}-${item.key}`}>
                <button
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(item)}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition',
                    i === active ? 'bg-primary-muted text-heading' : 'text-fg',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0 text-fg-subtle" />
                  <span className="truncate font-medium">{item.label}</span>
                  {item.context && <span className="truncate text-xs text-fg-subtle">{item.context}</span>}
                  <span className="kicker ml-auto !text-fg-subtle">{item.kind}</span>
                </button>
              </li>
            );
          })}
          {results.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-fg-muted">No matches.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
